import { Suspense, useMemo, useRef, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { ListChecksIcon } from 'lucide-react';
import { Link, type To, useSearchParams } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import {
  Doc_Template_Type_Enum_Enum,
  type ListingsMatchingFiltersPropertySearchesFragment,
  type UserBuyerLeadActionToolbarBuyerLeadFragment,
} from '@graphql/__generated__/graphql';
import {
  GET_DOC_TEMPLATES_FOR_DEFAULT,
  GET_PROPERTY_TYPES,
} from '@modules/_shared/common/queries';
import { BulkActionsMenu } from '@modules/_shared/components/buyer-lead/BulkActionsMenu';
import {
  GET_BUYER_LEAD_TOOLBAR_SUGGESTIONS,
  UPDATE_BUYER_LEADS_STAGE_MULTIPLE,
} from '@modules/_shared/components/buyer-lead/BuyerLeadActionToolbar';
import { BuyerLeadMoveToColumnDialog } from '@modules/_shared/components/buyer-lead/BuyerLeadMoveToColumnDialog';
import {
  type BuyerLeadKanbanStageOption,
  getBuyerLeadParentGlobalIds,
} from '@modules/_shared/components/buyer-lead/buyerLeadUtils';
import { CountBadge } from '@modules/_shared/components/CountBadge';
import {
  createValueFromString,
  withSnippet,
} from '@modules/_shared/components/email-editor/htmlValue';
import {
  parseSuggestionsMap,
  toSuggestionLanguage,
} from '@modules/_shared/components/email-form/contextSuggestions';
import { EmailFormDialog } from '@modules/_shared/components/email-form/EmailFormDialog';
import type { EmailFormRecipient } from '@modules/_shared/components/email-form/types';
import { SearchInput } from '@modules/_shared/components/search-input/SearchInput';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  getLotSnippetEntityData,
  toLegacyLot,
} from '@modules/_shared/utils/email';
import { useGetDefaultApolloTemplate } from '@modules/_shared/utils/template';
import { getDefaultListingsMatchingFilters } from '@modules/users/components/UserBuyerLeadMatchingDrawer';
import { GET_USER_BUYERS_LEAD } from '@modules/users/components/UserBuyerLeadsKanban';
import { useLocale } from '@shared/hooks/locale';

export const USER_BUYER_LEAD_TOOLBAR_BUYER_LEAD_FRAGMENT = gql(/* GraphQL */ `
  fragment UserBuyerLeadActionToolbarBuyerLead on buyer_leads {
    id
    stage_id
    user {
      id
      first_name
      emails(
        order_by: [{ primary: desc }, { verified: desc }, { created_at: desc }]
        limit: 1
      ) {
        id
        email
      }
    }
    lot {
      id
      tenant_id
      ...EmailTemplateFormListing
    }
  }
`);

type UserBuyerLeadActionToolbarProps = {
  propertySearches: ListingsMatchingFiltersPropertySearchesFragment[];
  buyerLeads: UserBuyerLeadActionToolbarBuyerLeadFragment[];
  onFilterChange: (value: string) => void;
  selectedBuyerLeads: string[];
  onReset: () => void;
  columns: BuyerLeadKanbanStageOption[];
  forceSelectionMode: boolean;
  toggleSelectionMode: () => void;
  stages: {
    matched?: string;
    matchSent?: string;
  };
};

type ActionsMenuProps = {
  selectedLeads: UserBuyerLeadActionToolbarBuyerLeadFragment[];
  columns: BuyerLeadKanbanStageOption[];
  onReset: () => void;
  recipient: UserBuyerLeadActionToolbarBuyerLeadFragment['user'] | null;
  stages: {
    matched?: string;
    matchSent?: string;
  };
};

const ActionsMenu: React.FC<ActionsMenuProps> = ({
  selectedLeads,
  columns,
  onReset,
  recipient,
  stages,
}) => {
  const { t, language } = useLocale();
  const { me } = useAppData();
  const { getDefaultTemplate } = useGetDefaultApolloTemplate();
  const [modalOpened, setModalOpened] = useState<'email' | 'moveColumn' | null>(
    null,
  );
  const suggestionLanguage = toSuggestionLanguage(language);

  const listingId = selectedLeads[0]?.lot.id ?? null;

  const { data: suggestionsData, loading: suggestionsLoading } = useQuery(
    GET_BUYER_LEAD_TOOLBAR_SUGGESTIONS,
    {
      variables: {
        senderId: me?.id,
        recipients: recipient?.id != null ? [recipient.id] : [],
        listingId,
        language: suggestionLanguage,
      },
      skip: me?.id == null || (listingId == null && recipient?.id == null),
    },
  );

  const { data: defaultBrochureAppraisalTemplates } = useQuery(
    GET_DOC_TEMPLATES_FOR_DEFAULT,
    {
      variables: {
        types: [
          Doc_Template_Type_Enum_Enum.LotBrochure,
          Doc_Template_Type_Enum_Enum.AppraisalReport,
        ],
      },
    },
  );

  const initialRecipients = useMemo<EmailFormRecipient[]>(() => {
    if (recipient == null) {
      return [];
    }

    const email = recipient.emails[0]?.email;
    if (email == null) {
      return [];
    }

    return [
      {
        id: recipient.id,
        email,
        name: recipient.first_name ?? undefined,
      },
    ];
  }, [recipient]);

  const templateIds = useMemo(
    () => ({
      brochure:
        getDefaultTemplate(
          defaultBrochureAppraisalTemplates?.doc_templates,
          Doc_Template_Type_Enum_Enum.LotBrochure,
        )?.id ?? null,
      appraisalReport:
        getDefaultTemplate(
          defaultBrochureAppraisalTemplates?.doc_templates,
          Doc_Template_Type_Enum_Enum.AppraisalReport,
        )?.id ?? null,
    }),
    [defaultBrochureAppraisalTemplates, getDefaultTemplate],
  );

  const templateSuggestions = useMemo(
    () =>
      parseSuggestionsMap(
        suggestionsData?.get_email_template_suggestions?.suggestions,
      ),
    [suggestionsData],
  );

  const listingSnippets = useMemo(
    () =>
      selectedLeads.map(lead => {
        const lot = toLegacyLot(lead.lot, templateIds.brochure);
        return getLotSnippetEntityData(lot, lead.lot.tenant_id, language);
      }),
    [selectedLeads, templateIds.brochure, language],
  );

  const initialEmailTemplate = useMemo(() => {
    const htmlStr = `<p>${t('Dear {{name}},', {
      name: recipient?.first_name ?? '',
    })}</p><br/><p>${t(
      'Based on your preferences, we think you might be interested in the following properties.',
    )}</p><br/><p>${t(
      "If these homes are not the perfect fit, don't worry! - we're continuously updating our listings and will notify you as soon as new matching properties become available.",
    )}</p><br/><p>${t(
      "Would you like to schedule a visit or receive more details? Don't hesitate to contact us.",
    )}</p><br/>`;

    let value = createValueFromString(htmlStr, 'html');
    for (const listingSnippetData of listingSnippets) {
      value = withSnippet(value, listingSnippetData);
    }

    return value;
  }, [listingSnippets, recipient?.first_name, t]);

  const parentGlobalIds = useMemo(
    () => getBuyerLeadParentGlobalIds(selectedLeads),
    [selectedLeads],
  );

  const [updateMultipleBuyerLeads] = useMutation(
    UPDATE_BUYER_LEADS_STAGE_MULTIPLE,
    {
      refetchQueries: [GET_USER_BUYERS_LEAD],
    },
  );

  return (
    <>
      <BulkActionsMenu
        disabled={selectedLeads.length === 0}
        emailDisabled={initialRecipients.length === 0 || suggestionsLoading}
        emailTooltipTitle={t('The current user does not have an email.')}
        emailTooltipDisabled={initialRecipients.length > 0}
        showEmailWarning={initialRecipients.length === 0}
        onSendEmail={() => setModalOpened('email')}
        onMoveToColumn={() => setModalOpened('moveColumn')}
      />
      <BuyerLeadMoveToColumnDialog
        columns={columns}
        selectedLeadIds={selectedLeads.map(lead => lead.id)}
        opened={modalOpened === 'moveColumn'}
        onClose={() => setModalOpened(null)}
        onReset={onReset}
        updateMultipleBuyerLeads={updateMultipleBuyerLeads}
      />
      <EmailFormDialog
        initialRecipients={initialRecipients}
        templateSuggestions={templateSuggestions}
        open={modalOpened === 'email'}
        onClose={() => setModalOpened(null)}
        campaignMode={false}
        initialSubject={t('Listings that might interest you')}
        initialBodyValue={initialEmailTemplate}
        parentGlobalIds={parentGlobalIds}
        onEmailSent={() => {
          if (stages.matchSent == null || stages.matched == null) {
            setModalOpened(null);
            onReset();
            return;
          }

          const leadsToUpdate = selectedLeads.filter(
            lead => lead.stage_id === stages.matched,
          );

          if (leadsToUpdate.length === 0) {
            setModalOpened(null);
            onReset();
            return;
          }

          updateMultipleBuyerLeads({
            variables: {
              buyerLeadIds: leadsToUpdate.map(lead => lead.id),
              stageId: stages.matchSent,
            },
            onCompleted: data => {
              if (data.update_buyer_leads != null) {
                setModalOpened(null);
                onReset();
              }
            },
          });
        }}
      />
    </>
  );
};

export const UserBuyerLeadActionToolbar: React.FC<
  UserBuyerLeadActionToolbarProps
> = ({
  propertySearches,
  onFilterChange,
  buyerLeads,
  selectedBuyerLeads,
  onReset,
  columns,
  toggleSelectionMode,
  forceSelectionMode,
  stages,
}) => {
  const { t } = useLocale();
  const { breakpoints } = useTheme();
  const [searchParams] = useSearchParams();
  const initialSearchParams = useRef(searchParams);

  // Don't need to catch the error here because it shouldn't prevent to use the toolbar.
  const { data: propertyTypesData } = useQuery(GET_PROPERTY_TYPES);

  const buyerLeadsMatchingLink = useMemo<To>(() => {
    const generatedSearchParams = getDefaultListingsMatchingFilters(
      propertySearches,
      propertyTypesData?.property_types ?? [],
      initialSearchParams.current,
    );

    return {
      pathname: 'matching',
      search: generatedSearchParams.toString(),
    };
  }, [propertySearches, propertyTypesData?.property_types]);

  const selectionInProgress =
    forceSelectionMode || selectedBuyerLeads.length > 0;
  const selectedLeadsData = useMemo(() => {
    return buyerLeads.filter(lead => selectedBuyerLeads.includes(lead.id));
  }, [selectedBuyerLeads, buyerLeads]);
  const recipient = selectedLeadsData[0]?.user ?? null;

  return (
    <>
      <Stack
        direction="row"
        spacing={{ xs: 0, sm: 2 }}
        sx={{
          px: 2,
          py: 1,
          alignItems: 'center',
          justifyContent: 'end',
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            [breakpoints.down('sm')]: {
              order: 1,
            },
          }}
        >
          {selectionInProgress ? (
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {t('{{count}} Listings selected', {
                count: selectedBuyerLeads.length,
              })}
            </Typography>
          ) : (
            <>
              <Typography
                variant="body1"
                component="span"
                sx={{ fontWeight: 600 }}
              >
                {t('Listings')}
              </Typography>
              {buyerLeads.length > 0 && (
                <CountBadge count={buyerLeads.length} />
              )}
            </>
          )}
        </Box>
        <Box
          sx={{
            [breakpoints.down('sm')]: {
              mt: '8px !important',
              order: 3,
              width: '100%',
            },
          }}
        >
          <SearchInput onValueChanged={onFilterChange} fullWidth />
        </Box>
        {selectionInProgress ? (
          <Stack
            spacing={2}
            direction="row"
            sx={{
              alignItems: 'center',
              [breakpoints.down('sm')]: {
                order: 2,
              },
            }}
          >
            <Button variant="text" disableElevation onClick={onReset}>
              {t('Cancel')}
            </Button>
            <Suspense fallback={null}>
              <ActionsMenu
                selectedLeads={selectedLeadsData}
                columns={columns}
                onReset={onReset}
                stages={stages}
                recipient={
                  recipient?.emails[0]?.email != null ? recipient : null
                }
              />
            </Suspense>
          </Stack>
        ) : (
          <>
            <Button
              variant="text"
              startIcon={<ListChecksIcon />}
              onClick={() => {
                toggleSelectionMode();
              }}
              disableElevation
              sx={{
                [breakpoints.down('sm')]: {
                  order: 2,
                },
              }}
            >
              {t('Select')}
            </Button>
            <Button
              variant="contained"
              disableElevation
              component={Link}
              to={buyerLeadsMatchingLink}
              sx={{
                [breakpoints.down('sm')]: {
                  order: 2,
                },
              }}
            >
              {t('Matching')}
            </Button>
          </>
        )}
      </Stack>
    </>
  );
};
